import React, { useEffect, useState } from "react";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import DoctorSideBar from "./Doctor-Sidebar";
import Youroheader from "../Youro-header";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import "../../styles/Doctor-ui/Doctor-appointment/Doctor-Appointment-page.css";
import "../../styles/Doctor-ui/DoctorPrescriptions.css";

const TABS = {
  SENT: "sent",
  REQUESTS: "requests",
};

const DoctorPrescriptions = () => {
  const [tab, setTab] = useState(TABS.REQUESTS);
  const [refills, setRefills] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  // actioningId disables the row being decided; the guard stops a second click
  // landing before that re-render, on this row or another.
  const [, guard] = useSubmitGuard();

  const docId = Cookies.get(COOKIE_KEYS.userId);
  const base =
    API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension;

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Two different things, from two different places. Requests are refills a
      // patient raised; Sent is what this provider prescribed on care plans.
      // A care plan never creates a refill row, so the two cannot share a source.
      const [refillRes, sentRes] = await Promise.all([
        axios.get(`${base}/refillRequestsByDoctor/${docId}`),
        axios.get(`${base}/prescriptionsSentByDoctor/${docId}`),
      ]);
      setRefills(Array.isArray(refillRes.data) ? refillRes.data : []);
      setSent(Array.isArray(sentRes.data) ? sentRes.data : []);
    } catch (err) {
      toast.error(
        errorMessage(err, "We couldn't load your prescriptions. Refresh the page to try again.")
      );
      setRefills([]);
      setSent([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = guard(async (refillId, approve) => {
    setActioningId(refillId);
    const path = approve ? "approveRefill" : "rejectRefill";
    try {
      await axios.post(`${base}/${path}/${refillId}`);
      toast.success(approve ? "Refill approved." : "Refill rejected.");
      await fetchAll();
    } catch (err) {
      toast.error(
        errorMessage(err, `We couldn't ${approve ? "approve" : "reject"} that refill request. Refresh and try again.`)
      );
    } finally {
      setActioningId(null);
    }
  });

  // presRefills.presTypes is keyed by prescription type id, each holding a list
  // of prescription details. Flatten it for display.
  const itemsOf = (refill) => {
    const byType = refill?.presRefills?.presTypes || {};
    return Object.values(byType).flat();
  };

  // Requests are what patients have asked for and not yet been answered.
  // Anything already approved or rejected leaves the inbox.
  const pending = refills.filter((r) => r.status === "PENDING");

  const renderItems = (refill) => {
    const items = itemsOf(refill);
    if (items.length === 0) {
      return <p className="rx-empty-items">No prescription details recorded.</p>;
    }
    return (
      <ul className="rx-items">
        {items.map((item, i) => (
          <li key={item.presId || i}>
            <span className="rx-item-name">{item.presName || item.patientLabel}</span>
            {item.dosage ? <span className="rx-item-meta">Quantity: {item.dosage}</span> : null}
            {item.refillCount ? (
              <span className="rx-item-meta">Refills: {item.refillCount}</span>
            ) : null}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      {/* rx-screen scopes the sticky sidebar rule to this page: the list grows
          past .d-app's 93vh, so the document scrolls and the nav would otherwise
          scroll away with it. */}
      <div className="d-app rx-screen">
        <div className="sidebar">
          <DoctorSideBar data={"doctor-prescriptions"} />
        </div>
        <div className="d-calender">
          <Youroheader />

          <div className="rx-page">
            <div className="rx-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={tab === TABS.REQUESTS}
                className={"rx-tab" + (tab === TABS.REQUESTS ? " rx-tab-active" : "")}
                onClick={() => setTab(TABS.REQUESTS)}
              >
                Prescription Requests
                {pending.length > 0 && <span className="rx-count">{pending.length}</span>}
              </button>
              <button
                role="tab"
                aria-selected={tab === TABS.SENT}
                className={"rx-tab" + (tab === TABS.SENT ? " rx-tab-active" : "")}
                onClick={() => setTab(TABS.SENT)}
              >
                Prescriptions Sent
              </button>
            </div>

            {loading && <p className="rx-status">Loading…</p>}

            {!loading && tab === TABS.REQUESTS && (
              pending.length === 0 ? (
                <p className="rx-status">No prescription requests waiting on you.</p>
              ) : (
                <div className="rx-list">
                  {pending.map((refill) => (
                    <article className="rx-card" key={refill.refillId}>
                      <header className="rx-card-head">
                        <div>
                          <h3 className="rx-card-title">Request #{refill.refillId}</h3>
                          <p className="rx-card-date">
                            Requested{" "}
                            {refill.requestedAt
                              ? moment(refill.requestedAt).format("MMMM Do YYYY, h:mm A")
                              : "—"}
                          </p>
                        </div>
                        <span className="rx-status-badge rx-status-pending">
                          {refill.status}
                        </span>
                      </header>

                      {renderItems(refill)}

                      <div className="rx-actions">
                        <button
                          className="rx-reject"
                          disabled={actioningId === refill.refillId}
                          onClick={() => decide(refill.refillId, false)}
                        >
                          Reject
                        </button>
                        <button
                          className="rx-approve"
                          disabled={actioningId === refill.refillId}
                          onClick={() => decide(refill.refillId, true)}
                        >
                          {actioningId === refill.refillId ? "Working…" : "Approve"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )
            )}

            {!loading && tab === TABS.SENT && (
              sent.length === 0 ? (
                <p className="rx-status">No prescriptions sent yet.</p>
              ) : (
                <div className="rx-list">
                  {sent.map((cp) => (
                    // Two kinds of row share this list: prescriptions authored on
                    // a care plan, and refills the provider approved. A refill has
                    // no care plan, so carePlanId is 0 on every one of them —
                    // keying on it alone would collide across refills.
                    <article
                      className="rx-card"
                      key={
                        cp.source === "REFILL"
                          ? `refill-${cp.refillId}`
                          : `careplan-${cp.carePlanId}`
                      }
                    >
                      <header className="rx-card-head">
                        <div>
                          <h3 className="rx-card-title">
                            {cp.patientName || "Patient"}
                          </h3>
                          <p className="rx-card-date">
                            {cp.diagnosisName ? cp.diagnosisName + " · " : ""}
                            Sent{" "}
                            {cp.sentOn
                              ? moment(cp.sentOn).format("MMMM Do YYYY, h:mm A")
                              : "—"}
                          </p>
                        </div>
                        {cp.source === "REFILL" && (
                          <span className="rx-badge-refill">Refill</span>
                        )}
                      </header>

                      <ul className="rx-items">
                        {(cp.items || []).map((item, i) => (
                          <li key={item.presId || i}>
                            <span className="rx-item-name">
                              {item.presName || item.patientLabel}
                            </span>
                            {item.dosage ? (
                              <span className="rx-item-meta">Quantity: {item.dosage}</span>
                            ) : null}
                            {item.refillCount ? (
                              <span className="rx-item-meta">
                                Refills: {item.refillCount}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorPrescriptions;
